function StatusDot({ ok, label }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${ok ? 'bg-emerald-500' : 'bg-red-500'}`} />
      <span className="text-sm text-gray-600">{label}</span>
    </div>
  );
}

function formatRelative(isoString) {
  if (!isoString) return null;
  const ms = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function ETLStatusPanel({ health, schedule, jobs, etlServiceReachable, loading }) {
  const dbOk = health?.db_status === 'connected';
  const vpnOk = health?.vpn_status === 'connected' || health?.vpn_status === 'not_required';
  const vpnLabel = health?.vpn_status === 'not_required' ? 'VPN: not required' : `VPN: ${health?.vpn_status ?? '—'}`;

  // `jobs` is already sorted most-recent-first by the backend (JobStore.list_jobs).
  const finishedJobs = (jobs ?? []).filter((j) => j.status === 'success' || j.status === 'failed');
  const lastJob = finishedJobs[0] ?? null;
  const lastSuccess = finishedJobs.find((j) => j.status === 'success') ?? null;
  const nextRun = schedule?.next_run
    ? new Date(schedule.next_run).toLocaleString()
    : schedule?.cron_expression
    ? `cron: ${schedule.cron_expression}`
    : 'Not scheduled';

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">ETL Service</h2>
        <span
          className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            etlServiceReachable
              ? 'bg-emerald-50 text-emerald-700'
              : 'bg-red-50 text-red-600'
          }`}
        >
          {loading ? 'Connecting…' : etlServiceReachable ? 'Online' : 'Unreachable'}
        </span>
      </div>

      {etlServiceReachable && health ? (
        <div className="space-y-1.5">
          <StatusDot ok={dbOk} label={`Database: ${health.db_status ?? '—'}`} />
          <StatusDot ok={vpnOk} label={vpnLabel} />
          {lastJob && (
            <div className="mt-3 pt-3 border-t border-gray-100 text-xs space-y-1">
              <div className="flex items-center gap-2">
                <span
                  className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    lastJob.status === 'success' ? 'bg-emerald-500' : 'bg-red-500'
                  }`}
                />
                <span className="text-gray-500">
                  Last run ({lastJob.triggered_by ?? 'manual'}): {formatRelative(lastJob.finished_at ?? lastJob.started_at)} —{' '}
                  <span className={lastJob.status === 'success' ? 'text-emerald-700 font-medium' : 'text-red-600 font-medium'}>
                    {lastJob.status}
                  </span>
                </span>
              </div>
              {lastJob.status === 'failed' && lastJob.error && (
                <div className="text-red-600 pl-4" title={lastJob.error}>
                  {lastJob.error.length > 120 ? `${lastJob.error.slice(0, 120)}…` : lastJob.error}
                </div>
              )}
              {lastJob.status === 'failed' && (
                <div className="text-gray-500 pl-4">
                  Last successful run: {lastSuccess ? formatRelative(lastSuccess.finished_at) : 'none recorded'}
                </div>
              )}
            </div>
          )}
          {schedule && (
            <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500 space-y-1">
              <div>Next run: <span className="text-gray-700 font-medium">{nextRun}</span></div>
              {schedule.transformer_id && (
                <div>Transformer: <span className="text-gray-700 font-medium">{schedule.transformer_id}</span></div>
              )}
            </div>
          )}
        </div>
      ) : (
        !loading && (
          <p className="text-sm text-gray-400">
            ETL service is not reachable. Start it with{' '}
            <code className="text-xs bg-gray-100 px-1 rounded">docker compose -f etl-service/docker-compose.local.yml up -d</code>
          </p>
        )
      )}
    </div>
  );
}
